/**
 * ContactDetail pure components.
 * These components receive all data as props instead of fetching via tRPC,
 * making them usable in both NextJS (with tRPC wrappers) and Electron (with IPC).
 */

export {
  ReminderActionCardPure,
  ViewReminderActionCardPure,
  CadenceReminderActionCardPure,
  CreateEditReminderActionPure,
} from './ReminderActionCardPure';

export { invalidReasonLabel, lineTypeBadgeLabel, sourceLabel } from './ContactFieldReadOnly';

export { VoiceNoteActionCardPure } from './VoiceNoteActionCardPure';
export { VideoNoteActionCardPure } from './VideoNoteActionCardPure';

export { formatPhoneDisplay } from './formatPhoneDisplay';

export { getPreviousEmailThread, wasLastStepEmail } from './previousEmailThread';
export type { EmailSubjectInteraction, PreviousEmailThread } from './previousEmailThread';

export { ContactDetailTabs } from './ContactDetailTabs';
export type { ContactDetailTabsProps, ContactDetailTab } from './ContactDetailTabs';

export { ContactInteractSectionPure, LoadingSection } from './ContactInteractSectionPure';
export { InteractionHistoryItem, interactionHistoryAction, interactionHistoryId } from './InteractionHistoryItem';
export type { InteractionHistoryActions } from './InteractionHistoryItem';

export { ContactDataSectionPure } from './ContactDataSectionPure';
export { Scrollable } from '../Scrollable';
export type { ContactDataSectionPureProps } from './ContactDataSectionPure';

export { ContactImagesSectionPure } from './ContactImagesSectionPure';
export type {
  ContactImagesSectionPureProps,
  ContactImageVariants,
  ContactImageCacheVariants,
} from './ContactImagesSectionPure';

export { ContactEmploymentEducationSectionPure } from './ContactEmploymentEducationSectionPure';
export type {
  ContactEmploymentEducationSectionPureProps,
  ContactExperienceItem,
  ContactEducationItem,
} from './ContactEmploymentEducationSectionPure';

export { default as AddExperiencePopup } from './AddExperiencePopup';
export type { AddExperienceInput, OrgSearchOption, AddExperiencePopupProps } from './AddExperiencePopup';

export { ContactSignalsGrid } from './ContactSignalsGrid';
export type { ContactSignalGridItem, ContactSignalsGridProps } from './ContactSignalsGrid';

export { BucketTooltipBody } from './BucketTooltipBody';
export type { BucketTooltipBodyBucket, BucketTooltipBodyProps } from './BucketTooltipBody';

export { ContactOneOffActionSectionPure, defaultFormattedLabels } from './ContactOneOffActionSectionPure';
export type {
  ContactOneOffActionSectionPureProps,
  ActionSectionUserTeam,
  ActionStep,
  ActionStepConfig,
  PendingStep,
  ScheduleStatus,
  SendMode,
  StepType,
  StepEditorData,
  StepEditorCallbacks,
  ReminderCardData,
  ReminderCardCallbacks,
  VoiceNoteCardData,
  VoiceNoteCardCallbacks,
  VideoNoteCardCallbacks,
} from './ContactOneOffActionSectionPure';

export { ContactDetailPageCore } from './ContactDetailPageCore';
export type {
  ContactDetailPageCoreProps,
  ContactDetailData,
  ContactActiveCadence,
  ActionSectionData,
  RetryState,
  TabId,
} from './ContactDetailPageCore';

export { CampaignSwitcherPure } from './CampaignSwitcherPure';
export type { CampaignSwitcherPureProps, SwitcherCampaign } from './CampaignSwitcherPure';

export { ContactDetailPageSkeleton } from './ContactDetailPageSkeleton';
export type { ContactDetailPageSkeletonProps } from './ContactDetailPageSkeleton';

export { ContactDetailPageNotFound } from './ContactDetailPageNotFound';
export type { ContactDetailPageNotFoundProps } from './ContactDetailPageNotFound';

// Re-export CadenceStep types for respond section
export type {
  StepData,
  StepChanges,
  AiChoice,
  EditorDocument,
  StepListItem,
} from '../CadenceStep';

// Re-export campaign button types for convenience
export type { TemplateOption, OwnerOption, StagingCandidate } from '../Input2/CampaignButtonWithCadence';

export { default as DetailHeader } from './DetailHeader';
export type { DetailHeaderProps } from './DetailHeader';

export {
  default as DetailHeaderContactPure,
  DetailHeaderContactLoading,
} from './DetailHeaderContactPure';
export type { DetailHeaderContactPureProps } from './DetailHeaderContactPure';

export { default as AvatarGroup } from './AvatarGroup';
export type { AvatarGroupProps } from './AvatarGroup';

export type {
  ContactDetailUserTeam,
  TeamUserOption,
  GlobalReminderWithUser,
  CadenceReminderData,
  ReminderActionData,
  AudioDropFileData,
  HistoryData,
  HistoryItem,
  ContactFieldName,
  MeetingBookedData,
  ContactDetailMutations,
  // Query-related types
  Stage,
  PendingStepData,
  ScheduleStatusData,
  EmailCredentialOption,
  EmailOption,
  LinkedInMappingData,
  CampaignTemplateOption,
  CampaignOwnerOption,
  EditorDocumentOption,
  CadenceTemplateInfoData,
  CadenceDocumentData,
  AIMessageTemplateData,
  ContactDetailQueries,
} from './types';

export type {
  ReminderActionCardPureProps,
  ViewReminderActionCardPureProps,
  CadenceReminderActionCardPureProps,
  CreateEditReminderActionPureProps,
} from './ReminderActionCardPure';

export type { VoiceNoteActionCardPureProps } from './VoiceNoteActionCardPure';
export type { VideoNoteActionCardPureProps } from './VideoNoteActionCardPure';

export type {
  ContactInteractSectionPureProps,
  CadenceData,
  CadenceSectionRenderProps,
  SequenceViewMode,
} from './ContactInteractSectionPure';

export type { LinkedInMapping } from '../LinkedInIdentifiersSection';

export { CadenceSectionPure } from './CadenceSectionPure';
export type {
  CadenceSectionPureProps,
  CadenceStepState,
  CadenceGraph,
  CadenceData as CadenceSectionCadenceData,
  TemplateInfo,
  ContactInfo as CadenceSectionContactInfo,
  DocumentData,
  StepCreateInput as CadenceSectionStepCreateInput,
  StepUpdateInput,
  AIMessageSectionData,
} from './CadenceSectionPure';

// Action section sub-components
export { ActionCardPure } from './ActionCardPure';
export type { ActionCardPureProps, ActionCardIconType } from './ActionCardPure';

export { ScheduleStatusBannerPure } from './ScheduleStatusBannerPure';
export type { ScheduleStatusBannerPureProps } from './ScheduleStatusBannerPure';

export { PendingStepBannerPure } from './PendingStepBannerPure';
export type { PendingStepBannerPureProps } from './PendingStepBannerPure';

export { UnipileConnectBannerPure } from './UnipileConnectBannerPure';
export type { UnipileConnectBannerPureProps } from './UnipileConnectBannerPure';

export { ContactDetailSlotsProvider, useContactDetailSlots } from './contactDetailSlots';
export type { ContactDetailSlots } from './contactDetailSlots';

export { ProspectDetailPure } from './ProspectDetailPure';
export type { ProspectDetailPureProps } from './ProspectDetailPure';
